import type { TypedSupabaseClient } from '../client';
import type {
  AttributePreset,
  AttributePresetValue,
  IArticleAttributePresetRepository,
  SaveAttributePresetInput,
} from '../../../interfaces/src';

// Same escape hatch as the variant repository: these tables are absent from
// the hand-maintained generated types. tenant_id is filled by the
// `*_set_tenant_id` BEFORE INSERT trigger, so inserts omit it.
type UntypedFrom = { from: (table: string) => any };

const READ_PRESETS = 'authorized_article_attribute_presets';
const READ_VALUES = 'authorized_article_attribute_preset_values';

function newPresetId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    },
  );
}

export class SupabaseArticleAttributePresetRepository implements IArticleAttributePresetRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private sb(): UntypedFrom {
    return this.supabase as unknown as UntypedFrom;
  }

  async list(): Promise<AttributePreset[]> {
    const sb = this.sb();
    const { data: presetRows, error } = await sb
      .from(READ_PRESETS)
      .select('*')
      .order('display_order');
    if (error) throw error;
    const presets = (presetRows ?? []) as Record<string, unknown>[];
    if (presets.length === 0) return [];

    const { data: valueRows, error: e2 } = await sb
      .from(READ_VALUES)
      .select('*')
      .in(
        'preset_id',
        presets.map((p) => p['id'] as string),
      )
      .order('display_order');
    if (e2) throw e2;

    const byPreset = new Map<string, AttributePresetValue[]>();
    for (const v of (valueRows ?? []) as Record<string, unknown>[]) {
      const presetId = v['preset_id'] as string;
      const list = byPreset.get(presetId) ?? [];
      list.push(mapValue(v));
      byPreset.set(presetId, list);
    }

    return presets.map((p) => ({
      id: p['id'] as string,
      name: (p['name'] as string) ?? '',
      displayOrder: (p['display_order'] as number) ?? 0,
      values: byPreset.get(p['id'] as string) ?? [],
    }));
  }

  async create(input: SaveAttributePresetInput): Promise<AttributePreset> {
    const id = newPresetId();
    await this.mutate('create', id, input);
    return this.getOne(id);
  }

  async update(
    id: string,
    input: SaveAttributePresetInput,
  ): Promise<AttributePreset> {
    await this.mutate('update', id, input);
    return this.getOne(id);
  }

  async delete(id: string): Promise<void> {
    await this.mutate('delete', id);
  }

  private async mutate(
    action: 'create' | 'update' | 'delete',
    presetId: string,
    input?: SaveAttributePresetInput,
  ): Promise<void> {
    const payload = input
      ? {
          name: input.name,
          display_order: input.displayOrder ?? 0,
          values: input.values.map((value, index) => ({
            ...(value.id ? { id: value.id } : {}),
            value: value.value,
            swatch: value.swatch ?? null,
            display_order: value.displayOrder ?? index,
          })),
        }
      : {};
    const { data, error } = (await this.supabase.rpc(
      'mutate_article_attribute_preset_atomic' as never,
      {
        p_action: action,
        p_preset_id: presetId,
        p_payload: payload,
      } as never,
    )) as { data: string | null; error: Error | null };
    if (error) throw error;
    if (data !== presetId) {
      throw new Error('article_attribute_preset_mutation_failed');
    }
  }

  private async getOne(id: string): Promise<AttributePreset> {
    const all = await this.list();
    const found = all.find((p) => p.id === id);
    if (!found) throw new Error(`Attribute preset ${id} not found after save`);
    return found;
  }
}

function mapValue(row: Record<string, unknown>): AttributePresetValue {
  return {
    id: row['id'] as string,
    presetId: row['preset_id'] as string,
    value: (row['value'] as string) ?? '',
    swatch: (row['swatch'] as string) ?? null,
    displayOrder: (row['display_order'] as number) ?? 0,
  };
}
